import { useEffect } from 'react';

/**
 * Pause `.marquee` CSS animations while the element is off-screen.
 *
 * Ported from the original `assets/site.js` IIFE: an IntersectionObserver
 * toggles the `is-out-of-view` class so site.css can suspend the animation
 * (saves CPU + lets users scroll back to a marquee and not miss content).
 *
 * Re-runs the query on every pathname change so new `.marquee` elements
 * mounted by route transitions get observed. Disconnects the observer on
 * cleanup to avoid leaks.
 */
export function useMarqueePause(pathname: string): void {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }
    const marquees = document.querySelectorAll<HTMLElement>('.marquee');
    if (marquees.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-out-of-view', !entry.isIntersecting);
        });
      },
      { threshold: 0 },
    );

    marquees.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [pathname]);
}
